// Unit arithmetic the EFP flow needs outside the plan: the commodity's bushel weight,
// and the basis a confirmation implies when it only states a net price.
import { PositionModel, Unit } from '../../../../Generated/Commodity-Management-Api';
import { convertPrice, convertVolume } from '../../../../Helpers/UnitConversionHelper';
import { EfpContractFields } from './EfpContractParser';

/**
 * Bushels-to-pounds for a commodity, read off the pricing strategy of any position that
 * already holds it. The commodity record itself does not carry one.
 */
export function findBushelWeight(positions: readonly PositionModel[], commodityId: number | undefined): number | undefined {
    if (commodityId === undefined) return undefined;
    return positions.find(position =>
        position.commodity?.commodityId === commodityId
        && position.positionPricingStrategy?.bushelVolume)?.positionPricingStrategy?.bushelVolume ?? undefined;
}

/** True when a price can be restated from one unit to another with what is known. */
export function canRestatePrice(from: Unit, to: Unit, bushelWeight: number | undefined): boolean {
    return from === to || convertVolume(1, from, to, bushelWeight) !== 1;
}

/** A price restated per another unit, to six places. */
export function restatePrice(value: number, from: Unit, to: Unit, bushelWeight: number | undefined): number {
    if (from === to) return value;
    return Number(convertPrice(value, from, to, bushelWeight).toFixed(6));
}

export interface EfpImpliedBasis {
    /** Net less board, per `unit`. */
    value: number;
    /** The net price's unit - the basis is implied in the same terms the net was quoted. */
    unit: Unit;
    netPrice: number;
    /** The board price restated per `unit`, so the subtraction can be shown. */
    boardPrice: number;
}

/**
 * The basis a confirmation implies when it gives a net price and a board price but no
 * basis: net = board + basis, so basis = net - board, once both are per the same thing.
 *
 * Nothing is implied while the board price's unit is unknown - subtracting a per-bushel
 * board from a per-tonne net is exactly the mistake this exists to avoid - or when the
 * two units cannot be reconciled because no bushel weight is known.
 */
export function impliedBasis(fields: EfpContractFields, bushelWeight: number | undefined): EfpImpliedBasis | undefined {
    const { netPrice, netPriceUnit, boardPrice, boardPriceUnit } = fields;
    if (netPrice === undefined || !netPriceUnit || boardPrice === undefined || !boardPriceUnit) return undefined;
    if (!canRestatePrice(boardPriceUnit, netPriceUnit, bushelWeight)) return undefined;

    const boardInNetUnit = restatePrice(boardPrice, boardPriceUnit, netPriceUnit, bushelWeight);

    return {
        value: Number((netPrice - boardInNetUnit).toFixed(6)),
        unit: netPriceUnit,
        netPrice,
        boardPrice: boardInNetUnit,
    };
}
